/**
 * Form Validation Utilities for Paper Submission
 */

// File validation constants
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

export const FILE_TYPES = {
    PDF: ['application/pdf'],
    DOC: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    ALL: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
};

/**
 * Validate file type
 * @param {File} file - File object to validate
 * @param {Array} allowedTypes - Array of allowed MIME types
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateFileType = (file, allowedTypes = FILE_TYPES.ALL) => {
    if (!file || !file.type) {
        return { valid: false, message: 'File is required' };
    }
    
    if (!allowedTypes.includes(file.type)) {
        return { 
            valid: false, 
            message: 'File type not allowed. Please upload PDF or DOC files only.' 
        };
    }
    
    return { valid: true, message: '' };
};

/**
 * Validate file size
 * @param {File} file - File object to validate
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateFileSize = (file, maxSize = MAX_FILE_SIZE) => {
    if (!file) {
        return { valid: false, message: 'File is required' };
    }
    
    if (file.size > maxSize) {
        const sizeMB = (maxSize / 1024 / 1024).toFixed(1);
        return { 
            valid: false, 
            message: `File size exceeds maximum limit of ${sizeMB}MB` 
        };
    }
    
    return { valid: true, message: '' };
};

/**
 * Validate file (both type and size)
 * @param {File} file - File object to validate
 * @param {Array} allowedTypes - Array of allowed MIME types
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateFile = (file, allowedTypes = FILE_TYPES.ALL, maxSize = MAX_FILE_SIZE) => {
    const typeValidation = validateFileType(file, allowedTypes);
    if (!typeValidation.valid) return typeValidation;
    
    const sizeValidation = validateFileSize(file, maxSize);
    if (!sizeValidation.valid) return sizeValidation;
    
    return { valid: true, message: '' };
};

/**
 * Validate text field
 * @param {string} value - Text value to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateText = (value, options = {}) => {
    const {
        minLength = 0,
        maxLength = Infinity,
        required = false,
        fieldName = 'Field'
    } = options;
    
    if (!value || value.trim() === '') {
        if (required) {
            return { valid: false, message: `${fieldName} is required` };
        }
        return { valid: true, message: '' };
    }
    
    const trimmedValue = value.trim();
    
    if (trimmedValue.length < minLength) {
        return { 
            valid: false, 
            message: `${fieldName} must be at least ${minLength} characters` 
        };
    }
    
    if (trimmedValue.length > maxLength) {
        return { 
            valid: false, 
            message: `${fieldName} must not exceed ${maxLength} characters` 
        };
    }
    
    return { valid: true, message: '' };
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateEmail = (email) => {
    if (!email || email.trim() === '') {
        return { valid: false, message: 'Email is required' };
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return { valid: false, message: 'Please enter a valid email address' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Validate phone number (Indonesian format)
 * @param {string} phone - Phone number to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') {
        return { valid: false, message: 'Phone number is required' };
    }
    
    // Remove spaces, dashes, and parentheses
    const cleaned = phone.replace(/[\s-()]/g, '');
    
    // Indonesian phone format: starts with 0, 62, or +62, followed by 9-13 digits
    const phoneRegex = /^(0|62|\+62)[0-9]{9,13}$/;
    
    if (!phoneRegex.test(cleaned)) {
        return { 
            valid: false, 
            message: 'Please enter a valid phone number (e.g., 081234567890 or 6281234567890)' 
        };
    }
    
    return { valid: true, message: '' };
};

/**
 * Validate organization/institution name
 * @param {string} org - Organization name to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateOrganization = (org) => {
    return validateText(org, {
        minLength: 1,
        maxLength: 200,
        required: true,
        fieldName: 'Organization'
    });
};

/**
 * Validate paper title
 * @param {string} title - Paper title to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validatePaperTitle = (title) => {
    return validateText(title, {
        minLength: 10,
        maxLength: 500,
        required: true,
        fieldName: 'Paper title'
    });
};

/**
 * Validate author name
 * @param {string} name - Name to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validateAuthorName = (name) => {
    return validateText(name, {
        minLength: 3,
        maxLength: 100,
        required: true,
        fieldName: 'Author name'
    });
};

/**
 * Validate complete form data for paper submission
 * @param {FormData} formData - Form data object
 * @param {boolean} isGroup - Whether this is a group submission
 * @param {string} category - Category (Umum/Mahasiswa)
 * @returns {Object} - { valid: boolean, errors: Array }
 */
export const validatePaperSubmissionForm = (formData, isGroup = false, category = 'Umum') => {
    const errors = [];
    
    // Validate required fields
    const requiredFields = [
        { name: 'jenis_paper_text', label: 'Theme' },
        { name: 'sub_tema_text', label: 'Sub Theme' },
        { name: 'kategori', label: 'Category' },
        { name: 'keikutsertaan', label: 'Participation Type' },
        { name: 'judul', label: 'Paper Title' },
        { name: 'email', label: 'Email' },
        { name: 'phone', label: 'Phone Number' },
        { name: 'organization', label: 'Organization' }
    ];
    
    requiredFields.forEach(field => {
        const value = formData.get(field.name);
        if (!value || value.trim() === '') {
            errors.push(`${field.label} is required`);
        }
    });
    
    // Validate email
    const email = formData.get('email');
    if (email) {
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            errors.push(emailValidation.message);
        }
    }
    
    // Validate phone
    const phone = formData.get('phone');
    if (phone) {
        const phoneValidation = validatePhone(phone);
        if (!phoneValidation.valid) {
            errors.push(phoneValidation.message);
        }
    }
    
    // Validate paper title
    const title = formData.get('judul');
    if (title) {
        const titleValidation = validatePaperTitle(title);
        if (!titleValidation.valid) {
            errors.push(titleValidation.message);
        }
    }
    
    // Validate organization
    const org = formData.get('organization');
    if (org) {
        const orgValidation = validateOrganization(org);
        if (!orgValidation.valid) {
            errors.push(orgValidation.message);
        }
    }
    
    // Validate required files
    const requiredFiles = [
        { name: 'paper_file', label: 'Paper File' },
        { name: 'cv_file', label: 'CV File' },
        { name: 'pernyataan_file', label: 'Statement Letter' }
    ];
    
    requiredFiles.forEach(fileField => {
        const file = formData.get(fileField.name);
        if (!file || !file.name) {
            errors.push(`${fileField.label} is required`);
        } else {
            const fileValidation = validateFile(file);
            if (!fileValidation.valid) {
                errors.push(`${fileField.label}: ${fileValidation.message}`);
            }
        }
    });
    
    // Validate group submission additional fields
    if (isGroup) {
        // Second author (required)
        const nama2 = formData.get('nama_2');
        if (!nama2 || nama2.trim() === '') {
            errors.push('Second author name is required');
        } else {
            const nameValidation = validateAuthorName(nama2);
            if (!nameValidation.valid) {
                errors.push(`Second author: ${nameValidation.message}`);
            }
        }

        const instansi2 = formData.get('instansi_2');
        if (!instansi2 || instansi2.trim() === '') {
            errors.push('Second author institution is required');
        }

        const phone2 = formData.get('phone_2');
        if (!phone2 || phone2.trim() === '') {
            errors.push('Second author phone number is required');
        } else {
            const phoneValidation = validatePhone(phone2);
            if (!phoneValidation.valid) {
                errors.push(`Second author: ${phoneValidation.message}`);
            }
        }

        const cv2 = formData.get('cv_2_file');
        if (!cv2 || !cv2.name) {
            errors.push('Second author CV is required');
        } else {
            const fileValidation = validateFile(cv2);
            if (!fileValidation.valid) {
                errors.push(`Second author CV: ${fileValidation.message}`);
            }
        }

        // Second author email (optional)
        const email2 = formData.get('email_2');
        if (email2 && email2.trim() !== '') {
            const emailValidation = validateEmail(email2);
            if (!emailValidation.valid) {
                errors.push(`Second author email: ${emailValidation.message}`);
            }
        }

        // Second author address (optional)
        const address2 = formData.get('address_2');
        if (address2 && address2.trim() !== '') {
            const addressValidation = validateText(address2, {
                minLength: 1,
                maxLength: 500,
                fieldName: 'Second author address'
            });
            if (!addressValidation.valid) {
                errors.push(`Second author address: ${addressValidation.message}`);
            }
        }

        // Third author (OPTIONAL - can be 2 or 3 members)
        const nama3 = formData.get('nama_3');
        const email3 = formData.get('email_3');
        const instansi3 = formData.get('instansi_3');
        const phone3 = formData.get('phone_3');
        const address3 = formData.get('address_3');
        const cv3 = formData.get('cv_3_file');

        // Only validate third author if at least one field is filled
        const hasThirdAuthor = (nama3 && nama3.trim() !== '') || 
                               (email3 && email3.trim() !== '') ||
                               (instansi3 && instansi3.trim() !== '') ||
                               (phone3 && phone3.trim() !== '') ||
                               (address3 && address3.trim() !== '') ||
                               (cv3 && cv3.name);

        if (hasThirdAuthor) {
            // If providing third author, name is required
            if (!nama3 || nama3.trim() === '') {
                errors.push('Third author name is required when adding third author');
            } else {
                const nameValidation = validateAuthorName(nama3);
                if (!nameValidation.valid) {
                    errors.push(`Third author: ${nameValidation.message}`);
                }
            }

            // Email optional but validate if provided
            if (email3 && email3.trim() !== '') {
                const emailValidation = validateEmail(email3);
                if (!emailValidation.valid) {
                    errors.push(`Third author email: ${emailValidation.message}`);
                }
            }

            // Address optional but validate if provided
            if (address3 && address3.trim() !== '') {
                const addressValidation = validateText(address3, {
                    minLength: 1,
                    maxLength: 500,
                    fieldName: 'Third author address'
                });
                if (!addressValidation.valid) {
                    errors.push(`Third author address: ${addressValidation.message}`);
                }
            }
        }

        if (isGroup && category === 'Mahasiswa') {
            const pddikti2 = formData.get('pddikti_2_file');
            if (!pddikti2 || !pddikti2.name) {
                errors.push('Second author PDDIKTI proof is required');
            }
        }
    }
    
    // Validate student category additional files
    if (category === 'Mahasiswa') {
        const pddikti = formData.get('pddikti_file');
        if (!pddikti || !pddikti.name) {
            errors.push('PDDIKTI proof is required for students');
        } else {
            const fileValidation = validateFile(pddikti);
            if (!fileValidation.valid) {
                errors.push(`PDDIKTI proof: ${fileValidation.message}`);
            }
        }
    }
    
    // Validate agreement
    const agreement = formData.get('agreement');
    if (!agreement) {
        errors.push('You must agree to the terms and conditions');
    }
    
    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Format phone number to Indonesian standard
 * @param {string} phone - Phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    // Format if starts with 0
    if (cleaned.startsWith('0')) {
        return '+62' + cleaned.substring(1);
    }
    
    // Format if starts with 62 without +
    if (cleaned.startsWith('62') && !cleaned.startsWith('+')) {
        return '+' + cleaned;
    }
    
    return cleaned;
};
